'use client'
import { useRef } from 'react'
import Link from 'next/link'
import {
  AdjustmentsHorizontalIcon,
  ArrowTopRightOnSquareIcon,
  BeakerIcon,
  BoltIcon,
  ChevronDownIcon,
  FunnelIcon,
  HomeModernIcon,
  RectangleGroupIcon,
  ShoppingBagIcon,
  SparklesIcon,
  SwatchIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'
import { getTrackedShopUrl } from '@/lib/external-links'
import { getShopCollectionPath, SHOP_COLLECTION_HANDLES } from '@/lib/shopify-catalog'

function buildShopHref(handle, utmContent) {
  return getTrackedShopUrl(getShopCollectionPath(handle), {
    utm_campaign: 'shop_navigation',
    utm_content: utmContent,
  })
}

const SHOP_ITEMS = [
  {
    name: 'Whole-Home Treatment',
    description: 'Compare complete home water-treatment options.',
    href: buildShopHref(SHOP_COLLECTION_HANDLES.wholeHomeWaterTreatment, 'whole_home'),
    icon: HomeModernIcon,
  },
  {
    name: 'Water Softeners',
    description: 'True soft water and hard-scale control.',
    href: buildShopHref(SHOP_COLLECTION_HANDLES.waterSofteners, 'water_softeners'),
    icon: FunnelIcon,
  },
  {
    name: 'Salt-Free Conditioners',
    description: 'Scale prevention without salt or regeneration.',
    href: buildShopHref(SHOP_COLLECTION_HANDLES.saltFreeWaterConditioners, 'salt_free_conditioners'),
    icon: SparklesIcon,
  },
  {
    name: 'Chlorine Removal',
    description: 'Reduce chlorine taste and odour at every tap.',
    href: buildShopHref(SHOP_COLLECTION_HANDLES.wholeHouseChlorineFilters, 'chlorine_removal'),
    icon: SwatchIcon,
  },
  {
    name: 'Reverse Osmosis',
    description: 'Tankless and under-sink drinking-water systems.',
    href: buildShopHref(SHOP_COLLECTION_HANDLES.reverseOsmosisSystems, 'reverse_osmosis'),
    icon: BeakerIcon,
  },
  {
    name: 'UV Systems',
    description: 'UV disinfection systems, lamps, and sleeves.',
    href: buildShopHref(SHOP_COLLECTION_HANDLES.uvSystems, 'uv_systems'),
    icon: BoltIcon,
  },
  {
    name: 'Replacement Filters & UV Parts',
    href: buildShopHref(SHOP_COLLECTION_HANDLES.waterFilterCartridges, 'replacement_filters'),
    icon: AdjustmentsHorizontalIcon,
  },
  {
    name: 'RO Faucets & Accessories',
    href: buildShopHref(SHOP_COLLECTION_HANDLES.roFaucetsAccessories, 'ro_accessories'),
    icon: RectangleGroupIcon,
  },
  {
    name: 'Installation & Service',
    href: buildShopHref(SHOP_COLLECTION_HANDLES.installationService, 'installation_service'),
    icon: WrenchScrewdriverIcon,
  },
]

const PRIMARY_SHOP_ITEMS = SHOP_ITEMS.slice(0, 6)
const SUPPORT_SHOP_ITEMS = SHOP_ITEMS.slice(6)
const BEST_SELLERS_HREF = buildShopHref(SHOP_COLLECTION_HANDLES.bestSellers, 'best_sellers')
const ALL_PRODUCTS_HREF = buildShopHref(SHOP_COLLECTION_HANDLES.allProducts, 'all_products')

export function ShopDropdown({
  isOpen,
  onToggle,
  onOpen,
  onLeave,
  onClose,
  triggerRef,
}) {
  const openedFromPointerRef = useRef(false)
  const suppressNextPointerClickRef = useRef(false)

  return (
    <div
      className="static"
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') {
          if (!isOpen) openedFromPointerRef.current = true
          onOpen()
        }
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') {
          onLeave()
        }
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onClose()
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        onPointerDown={(event) => {
          if (event.pointerType === 'mouse' && isOpen && openedFromPointerRef.current) {
            suppressNextPointerClickRef.current = true
          }
        }}
        onClick={() => {
          if (suppressNextPointerClickRef.current) {
            suppressNextPointerClickRef.current = false
            openedFromPointerRef.current = false
            onOpen()
            return
          }

          openedFromPointerRef.current = false
          onToggle()
        }}
        className={`inline-flex min-h-10 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[15px] font-medium tracking-[-0.01em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 ${
          isOpen
            ? 'bg-water-aqua text-water-blue-dark'
            : 'text-water-ink hover:bg-water-canvas hover:text-water-blue-dark'
        }`}
        aria-expanded={isOpen}
        aria-controls="desktop-shop-menu"
        aria-haspopup="true"
      >
        Shop
        <ChevronDownIcon
          className={`h-3.5 w-3.5 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen ? (
        <div
          id="desktop-shop-menu"
          className="absolute inset-x-0 top-full z-50 border-y border-water-border bg-white/95 shadow-[0_22px_55px_rgba(11,34,57,0.12)] backdrop-blur-xl"
          onPointerEnter={(event) => {
            if (event.pointerType === 'mouse') onOpen()
          }}
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse') onLeave()
          }}
        >
          <div className="animate-nav-menu mx-auto max-w-7xl px-6 py-7 lg:px-8">
            <div className="flex items-end justify-between gap-6 border-b border-water-border pb-5">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-water-blue-dark">Shop Water Treatment</p>
                <p className="mt-2 text-xl font-semibold tracking-[-0.02em] text-water-ink">Browse real systems by the job they do.</p>
              </div>
              <Link
                href={BEST_SELLERS_HREF}
                onClick={onClose}
                className="inline-flex min-h-11 flex-none items-center rounded-full bg-water-ink px-5 text-sm font-semibold text-white transition-colors hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
              >
                Shop Best Sellers
              </Link>
            </div>

            <div className="mt-4 grid gap-1 md:grid-cols-2 xl:grid-cols-3">
              {PRIMARY_SHOP_ITEMS.map((item) => {
                const Icon = item.icon

                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={onClose}
                    className="group flex min-w-0 gap-3 rounded-[1rem] p-4 transition-colors hover:bg-water-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-water-blue"
                  >
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-water-aqua text-water-blue-dark transition-colors group-hover:bg-white">
                      <Icon className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-water-ink">{item.name}</span>
                      <span className="mt-0.5 block text-sm leading-5 text-water-muted">{item.description}</span>
                    </span>
                  </Link>
                )
              })}
            </div>

            <div className="mt-4 grid gap-1 rounded-[1rem] bg-water-canvas p-1 sm:grid-cols-3">
              {SUPPORT_SHOP_ITEMS.map((item) => {
                const Icon = item.icon

                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={onClose}
                    className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-water-ink transition-colors hover:bg-white hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-water-blue"
                  >
                    <Icon className="h-4 w-4 flex-none text-water-muted" aria-hidden="true" />
                    {item.name}
                  </Link>
                )
              })}
            </div>

            <div className="mt-4 flex justify-end">
              <Link
                href={ALL_PRODUCTS_HREF}
                onClick={onClose}
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-water-border px-5 text-sm font-semibold text-water-ink transition-colors hover:border-water-blue/40 hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
              >
                View All Products
                <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function ShopMobileMenu({ onItemClick }) {
  return (
    <div className="mt-2 rounded-2xl bg-white p-2" id="mobile-shop-links">
      <div className="grid gap-1">
        {SHOP_ITEMS.map((item) => {
          const Icon = item.icon

          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={onItemClick}
              className="flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-water-ink transition-colors hover:bg-water-canvas hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-water-blue"
            >
              <Icon className="h-4 w-4 flex-none text-water-muted" aria-hidden="true" />
              {item.name}
            </Link>
          )
        })}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 border-t border-water-border pt-2">
        <Link
          href={BEST_SELLERS_HREF}
          onClick={onItemClick}
          className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-water-aqua px-3 text-sm font-semibold text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue"
        >
          <ShoppingBagIcon className="h-4 w-4" aria-hidden="true" />
          Best Sellers
        </Link>
        <Link
          href={ALL_PRODUCTS_HREF}
          onClick={onItemClick}
          className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-water-ink px-3 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          All Products
        </Link>
      </div>
    </div>
  )
}
